import {
  ReportBrandingIds,
  ReportIds,
  ReportInputParameterKeys,
  ReportInputParameterTypes,
  ReportNtpDisplayModes,
} from './enums';
import type { ReportId, ReportInputParameter } from './enums';

export { ReportBrandingIds, ReportIds, ReportInputParameterKeys, ReportInputParameterTypes, ReportNtpDisplayModes };
export type { ReportBrandingId, ReportId, ReportInputParameterType, ReportNtpDisplayMode } from './enums';
export type { ReportInputParameter } from './enums';

export const REPORT_SOURCE_TABLES = {
  paid: 'reporting.master_paid',
  submit: 'reporting.master_submit',
} as const;

export const REPORT_SOURCE_TABLE_ALIASES = {
  paid: 'reporting.master_paid as paid',
  submit: 'reporting.master_submit as submit',
} as const;

const baseDateParameters: readonly ReportInputParameter[] = [
  { key: ReportInputParameterKeys.startDate, type: ReportInputParameterTypes.date, required: true },
  { key: ReportInputParameterKeys.endDate, type: ReportInputParameterTypes.date, required: true },
];

const productionDisplayParameters: readonly ReportInputParameter[] = [
  { key: ReportInputParameterKeys.displayThreshold, type: ReportInputParameterTypes.number, required: false },
  { key: ReportInputParameterKeys.includeThreshold, type: ReportInputParameterTypes.boolean, required: false },
  { key: ReportInputParameterKeys.ntpDisplayMode, type: ReportInputParameterTypes.select, required: false },
  { key: ReportInputParameterKeys.showTotals, type: ReportInputParameterTypes.boolean, required: false },
];

/**
 * Only the personal reports list agents by name, so only they can hide one. The MGA
 * and RMD reports group agencies and RMDs: offering the switch there gave the viewer
 * a control whose only effect was to understate the rows it left on the page.
 */
const personalDisplayParameters: readonly ReportInputParameter[] = [
  // Keep suppression immediately before totals: this is both the intended form order
  // and the order readers use to reason about what a total includes.
  ...productionDisplayParameters.slice(0, -1),
  { key: ReportInputParameterKeys.showSuppressedAgents, type: ReportInputParameterTypes.boolean, required: false },
  { key: ReportInputParameterKeys.showTotals, type: ReportInputParameterTypes.boolean, required: false },
];

const rmdDisplayParameters: readonly ReportInputParameter[] = [
  { key: ReportInputParameterKeys.displayThreshold, type: ReportInputParameterTypes.number, required: false },
  { key: ReportInputParameterKeys.includeThreshold, type: ReportInputParameterTypes.boolean, required: false },
  { key: ReportInputParameterKeys.showTotals, type: ReportInputParameterTypes.boolean, required: false },
];

export const reportInputParametersById = {
  [ReportIds.mgaPaid]: [...baseDateParameters, ...productionDisplayParameters],
  [ReportIds.personalPaid]: [...baseDateParameters, ...personalDisplayParameters],
  [ReportIds.rmdPaid]: [...baseDateParameters, ...rmdDisplayParameters],
  [ReportIds.mgaSubmit]: [...baseDateParameters, ...productionDisplayParameters],
  [ReportIds.personalSubmit]: [...baseDateParameters, ...personalDisplayParameters],
  [ReportIds.rmdSubmit]: [...baseDateParameters, ...rmdDisplayParameters],
} as const satisfies Record<ReportId, readonly ReportInputParameter[]>;

export function isKnownReportId(value: string): value is ReportId {
  return (Object.values(ReportIds) as readonly string[]).includes(value);
}

export function getInputParametersByReportId(reportId: string): readonly ReportInputParameter[] | undefined {
  if (!isKnownReportId(reportId)) {
    return undefined;
  }

  return reportInputParametersById[reportId];
}

/** Whether a report takes this input at all — the parameter set is no longer uniform. */
export function reportSupportsInputParameter(reportId: string, key: ReportInputParameterKeys): boolean {
  return getInputParametersByReportId(reportId)?.some(parameter => parameter.key === key) ?? false;
}
